/**
 * Shared confirmation dialog and toast built on the .ms-modal and .ms-toast
 * components. They replace window.confirm/alert/prompt on account and listing
 * pages: confirmDialog() resolves true/false, promptDialog() resolves the
 * entered text or null, showToast() shows a polite status message.
 * openModal() gives a server-rendered .ms-modal the same keyboard behaviour.
 */

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function focusableIn(container) {
    return Array.from(container.querySelectorAll(FOCUSABLE))
        .filter((node) => !node.classList.contains('ms-modal__overlay') && node.offsetParent !== null);
}

// Keeps Tab and Shift+Tab inside a dialog: the last control wraps to the first
// and back. The overlay is not a stop; without any control the focus stays on
// `fallback`.
function trapTab(event, container, fallback) {
    const focusable = focusableIn(container);
    if (!focusable.length) { event.preventDefault(); fallback.focus(); return; }
    const index = focusable.indexOf(document.activeElement);
    if (event.shiftKey && index <= 0) { event.preventDefault(); focusable[focusable.length - 1].focus(); }
    else if (!event.shiftKey && (index === -1 || index === focusable.length - 1)) { event.preventDefault(); focusable[0].focus(); }
}

let modal = null;
// The dialog that currently owns the shared modal. Opening another dialog
// takes the modal over: the previous one is released, so a request it started
// can still settle its own Promise but can never close or edit the newer one.
let owner = null;

function buildModal() {
    const root = document.createElement('div');
    root.className = 'ms-modal';
    root.hidden = true;
    root.setAttribute('data-dialog', '');
    root.innerHTML = `
        <button class="ms-modal__overlay" type="button" data-dialog-cancel aria-label="Zamknij okno"></button>
        <section class="ms-modal__dialog" role="dialog" aria-modal="true" aria-labelledby="ms-dialog-title" tabindex="-1">
            <header class="ms-modal__head">
                <h2 class="ms-modal__title" id="ms-dialog-title"></h2>
                <button class="ms-modal__close" type="button" data-dialog-cancel aria-label="Zamknij okno">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"></path></svg>
                </button>
            </header>
            <div class="ms-modal__body">
                <div class="ms-stack ms-stack--tight">
                    <p data-dialog-body></p>
                    <label class="ms-field" data-dialog-field hidden>
                        <span class="ms-field__label" data-dialog-field-label></span>
                        <textarea class="ms-textarea" rows="4" data-dialog-input></textarea>
                    </label>
                    <div class="ms-alert ms-alert--danger" role="alert" data-dialog-error hidden></div>
                    <div class="ms-form__actions">
                        <button class="ms-button ms-button--secondary" type="button" data-dialog-cancel></button>
                        <button class="ms-button ms-button--primary" type="button" data-dialog-ok></button>
                    </div>
                </div>
            </div>
        </section>`;
    document.body.appendChild(root);
    return root;
}

/**
 * While `onSubmit` runs the dialog is busy: the confirm button shows the
 * loading state (and `submittingLabel`, when given), and Escape, „Anuluj”,
 * the close button and the overlay do nothing until the request settles.
 * A failed submit shows its error and makes the dialog usable again.
 */
function openDialog({ title, body = '', okLabel = 'Potwierdź', cancelLabel = 'Anuluj', submittingLabel = '', tone = 'primary', field = null, onSubmit = null }) {
    modal = modal || buildModal();
    const released = owner;
    if (released) released.release();
    const instance = {};
    owner = instance;
    const focusInModal = modal.contains(document.activeElement);
    instance.returnFocus = released && focusInModal ? released.returnFocus : document.activeElement;
    const dialogNode = modal.querySelector('.ms-modal__dialog');
    const titleNode = modal.querySelector('#ms-dialog-title');
    const bodyNode = modal.querySelector('[data-dialog-body]');
    const fieldNode = modal.querySelector('[data-dialog-field]');
    const input = modal.querySelector('[data-dialog-input]');
    const errorNode = modal.querySelector('[data-dialog-error]');
    const ok = modal.querySelector('[data-dialog-ok]');
    const cancelButtons = modal.querySelectorAll('[data-dialog-cancel]');
    const cancelResult = field ? null : false;

    const setBusy = (busy) => {
        // aria-disabled keeps the focus on the button while it reports progress.
        ok.setAttribute('aria-disabled', busy ? 'true' : 'false');
        ok.classList.toggle('is-loading', busy);
        ok.textContent = busy && submittingLabel ? submittingLabel : okLabel;
        cancelButtons.forEach((button) => { button.disabled = busy; });
        input.readOnly = busy;
        dialogNode.setAttribute('aria-busy', busy ? 'true' : 'false');
    };

    titleNode.textContent = title;
    bodyNode.textContent = body;
    bodyNode.hidden = body === '';
    // .ms-field is a flex box, so the hidden attribute alone would not hide it.
    fieldNode.hidden = field === null;
    fieldNode.style.display = field === null ? 'none' : '';
    if (field) {
        modal.querySelector('[data-dialog-field-label]').textContent = field.label;
        input.value = field.value || '';
        input.maxLength = field.maxLength || 2000;
    }
    errorNode.hidden = true;
    ok.className = 'ms-button ' + (tone === 'danger' ? 'ms-button--danger' : 'ms-button--primary');
    ok.disabled = false;
    setBusy(false);
    modal.querySelector('.ms-form__actions [data-dialog-cancel]').textContent = cancelLabel;
    modal.hidden = false;
    document.documentElement.classList.add('is-dialog-open');

    return new Promise((resolve) => {
        let submitting = false;
        let settled = false;
        const owns = () => owner === instance;
        const detach = () => {
            ok.removeEventListener('click', onOk);
            cancelButtons.forEach((button) => button.removeEventListener('click', onCancel));
            document.removeEventListener('keydown', onKey, true);
        };
        const settle = (result) => {
            if (settled) return;
            settled = true;
            if (owns()) {
                owner = null;
                detach();
                setBusy(false);
                modal.hidden = true;
                document.documentElement.classList.remove('is-dialog-open');
                const target = instance.returnFocus;
                if (target && typeof target.focus === 'function') target.focus();
            }
            resolve(result);
        };
        instance.release = () => {
            owner = null;
            detach();
            // A submit already sent keeps running and settles this Promise itself.
            if (!submitting) settle(cancelResult);
        };
        const onOk = async () => {
            if (submitting) return;
            const value = field ? input.value : true;
            if (!onSubmit) { settle(value); return; }
            submitting = true;
            errorNode.hidden = true;
            setBusy(true);
            try {
                await onSubmit(value);
            } catch (error) {
                submitting = false;
                if (!owns()) { settle(cancelResult); return; }
                setBusy(false);
                errorNode.textContent = (error && error.message) || 'Nie udało się zapisać zmian. Spróbuj ponownie.';
                errorNode.hidden = false;
                if (field) input.focus();
                return;
            }
            submitting = false;
            settle(value);
        };
        const onCancel = () => {
            if (!submitting) settle(cancelResult);
        };
        const onKey = (event) => {
            if (event.key === 'Escape') { event.preventDefault(); onCancel(); return; }
            if (event.key === 'Tab') trapTab(event, modal, dialogNode);
        };
        ok.addEventListener('click', onOk);
        cancelButtons.forEach((button) => button.addEventListener('click', onCancel));
        document.addEventListener('keydown', onKey, true);
        window.setTimeout(() => {
            if (!owns()) return;
            if (field) input.focus();
            else if (tone === 'danger') modal.querySelector('.ms-form__actions [data-dialog-cancel]').focus();
            else ok.focus();
        }, 0);
    });
}

export function confirmDialog(options) {
    return openDialog(options);
}

export function promptDialog(options) {
    return openDialog({ ...options, field: options.field || { label: '', value: '' } });
}

// The server-rendered modal that is open now (only one at a time).
let pageModal = null;

// Escape closes the innermost layer first: an expanded list of suggestions
// (the focus is in its combobox or in the list) or an open help popup closes
// on its own Escape, and only the next one closes the modal. A list that shows
// only a message (searching, no result, an error) is not expanded for a screen
// reader, but it is open on the screen: its combobox closes it first too.
function innerLayerOpen(event) {
    if (document.querySelector('.info-popup')) return true;
    const target = event.target;
    if (!target || !target.closest) return false;
    if (target.closest('[role="combobox"][aria-expanded="true"], [role="listbox"]')) return true;
    const combobox = target.closest('[role="combobox"]');
    const listId = combobox && combobox.getAttribute('aria-controls');
    const list = listId ? document.getElementById(listId) : null;
    return Boolean(list && !list.hidden);
}

/**
 * Opens a modal rendered with the page (.ms-modal markup whose close button,
 * overlay and „Anuluj” carry [data-modal-close]) with the behaviour of the
 * dialogs above: the focus moves to the first control of the body, Tab and
 * Shift+Tab stay inside, Escape and every [data-modal-close] control close it
 * (Escape first closes an open list of suggestions or help popup, see
 * innerLayerOpen), and the focus returns to `returnFocus` (by default the
 * control that opened it). Resolves when the modal closes.
 */
export function openModal(root, { returnFocus = document.activeElement } = {}) {
    if (!root) return Promise.resolve();
    if (pageModal) pageModal.close();
    const dialogNode = root.querySelector('[role="dialog"]') || root;
    return new Promise((resolve) => {
        const instance = {};
        const onClick = (event) => {
            if (!event.target.closest('[data-modal-close]')) return;
            event.preventDefault();
            instance.close();
        };
        const onKey = (event) => {
            // A confirmation opened on top of this modal owns the keyboard.
            if (owner) return;
            if (event.key === 'Escape') {
                if (innerLayerOpen(event)) return;
                event.preventDefault();
                instance.close();
                return;
            }
            if (event.key === 'Tab') trapTab(event, dialogNode, dialogNode);
        };
        instance.close = () => {
            if (pageModal !== instance) return;
            pageModal = null;
            root.removeEventListener('click', onClick);
            document.removeEventListener('keydown', onKey, true);
            root.hidden = true;
            document.documentElement.classList.remove('is-dialog-open');
            if (returnFocus && typeof returnFocus.focus === 'function' && returnFocus.isConnected !== false) returnFocus.focus();
            resolve();
        };
        pageModal = instance;
        root.hidden = false;
        document.documentElement.classList.add('is-dialog-open');
        root.addEventListener('click', onClick);
        document.addEventListener('keydown', onKey, true);
        const body = dialogNode.querySelector('.ms-modal__body');
        const first = (body && focusableIn(body)[0]) || focusableIn(dialogNode)[0];
        if (first) {
            first.focus();
        } else {
            if (!dialogNode.hasAttribute('tabindex')) dialogNode.setAttribute('tabindex', '-1');
            dialogNode.focus();
        }
    });
}


function toastRegion() {
    let region = document.querySelector('[data-toast-region]');
    if (!region) {
        region = document.createElement('div');
        region.className = 'ms-toast-region';
        region.setAttribute('data-toast-region', '');
        region.setAttribute('aria-live', 'polite');
        document.body.appendChild(region);
    }
    return region;
}

export function dismissToast(toast) {
    if (!toast || !toast.isConnected) return;
    toast.classList.add('is-leaving');
    window.setTimeout(() => toast.remove(), 220);
}

export function showToast(text, { actionLabel = '', onAction = null, timeout = 5000 } = {}) {
    const toast = document.createElement('div');
    toast.className = 'ms-toast';
    toast.setAttribute('role', 'status');
    toast.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12l5 5 9-10"></path></svg><span></span><button type="button"></button>';
    toast.querySelector('span').textContent = text;
    const button = toast.querySelector('button');
    button.textContent = actionLabel || 'Zamknij';
    button.addEventListener('click', () => {
        if (onAction) onAction();
        dismissToast(toast);
    });
    toastRegion().appendChild(toast);
    if (timeout > 0) window.setTimeout(() => dismissToast(toast), timeout);
    return toast;
}
